"use client";

import { useEffect, useMemo, useState } from "react";
import { Icon } from "./Icon";
import { parseDashboardMarkdown } from "@/lib/dashboard/parseDashboard";

interface CalendarItem {
  id: string;
  contactName: string;
  summary: string;
  date: string;
  time?: string;
  durationMin?: number;
  description?: string;
}

function extractCalendarItems(markdown: string): CalendarItem[] {
  const { contacts } = parseDashboardMarkdown(markdown);
  const items: CalendarItem[] = [];
  let idx = 0;

  for (const contact of contacts) {
    for (const section of contact.sections) {
      if (section.key !== "agenda") continue;
      const f = section.fields;
      const date = f["Date"];
      if (!date) continue;
      const summary = f["Titre"] || f["Résumé"] || `RDV — ${contact.name}`;
      const durationRaw = f["Durée"] || f["Duree"];
      items.push({
        id: `cal-${idx++}`,
        contactName: contact.name,
        summary,
        date,
        time: f["Heure"],
        durationMin: durationRaw ? parseInt(durationRaw, 10) || undefined : undefined,
        description: section.body,
      });
    }
  }
  return items;
}

type SendState =
  | { status: "checking" }
  | { status: "idle" }
  | { status: "sending" }
  | { status: "sent"; htmlLink: string }
  | { status: "duplicate"; htmlLink: string }
  | { status: "error"; error: string };

export function AddToCalendarPanel({ markdown }: { markdown: string }) {
  const items = useMemo(() => extractCalendarItems(markdown), [markdown]);
  if (items.length === 0) return null;

  return (
    <div className="mt-4 rounded-2xl glass-brutal overflow-hidden">
      <header className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-white/[0.03] backdrop-blur-xl">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#0a1410] text-white">
          <Icon name="Calendar" size={14} />
        </div>
        <div>
          <div className="text-sm font-semibold text-[#0a1410]">Rendez-vous détectés</div>
          <div className="text-[11px] text-[var(--color-muted)]">
            {items.length} événement{items.length > 1 ? "s" : ""} — création via Google Agenda
          </div>
        </div>
      </header>
      <div className="p-3 space-y-2">
        {items.map((item) => (
          <CalendarCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function CalendarCard({ item }: { item: CalendarItem }) {
  const [state, setState] = useState<SendState>({ status: "checking" });

  // Vérifie dès l'affichage si un événement équivalent existe déjà ce jour-là,
  // pour ne jamais proposer "Ajouter" sur un rendez-vous déjà dans l'agenda.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/integrations/calendar/create", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            summary: item.summary,
            date: item.date,
            checkOnly: true,
          }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.duplicate) {
          setState({ status: "duplicate", htmlLink: data.htmlLink });
        } else {
          setState({ status: "idle" });
        }
      } catch {
        if (!cancelled) setState({ status: "idle" });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.summary, item.date]);

  const create = async () => {
    setState({ status: "sending" });
    try {
      const res = await fetch("/api/integrations/calendar/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          summary: item.summary,
          date: item.date,
          time: item.time,
          durationMin: item.durationMin,
          description: item.description,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setState(
        data.duplicate
          ? { status: "duplicate", htmlLink: data.htmlLink }
          : { status: "sent", htmlLink: data.htmlLink }
      );
    } catch (e) {
      setState({ status: "error", error: e instanceof Error ? e.message : "Erreur" });
    }
  };

  const sent = state.status === "sent";
  const duplicate = state.status === "duplicate";
  const checking = state.status === "checking";

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-[#0a1410] truncate">{item.summary}</div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[var(--color-muted)]">
            <Icon name="Calendar" size={10} />
            <span>
              {item.date}
              {item.time ? ` · ${item.time}` : ""}
              {item.durationMin ? ` · ${item.durationMin} min` : ""}
            </span>
          </div>
          <div className="mt-0.5 text-[11px] text-[var(--color-muted)] truncate">{item.contactName}</div>
        </div>
        {sent ? (
          <a
            href={state.htmlLink}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 rounded-md bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[10px] font-semibold text-emerald-700"
          >
            <Icon name="CheckCircle" size={10} /> Ajouté — voir
          </a>
        ) : duplicate ? (
          <a
            href={state.htmlLink}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1 text-[10px] font-semibold text-amber-700"
            title="Un événement au même titre existe déjà ce jour-là — rien n'a été recréé."
          >
            <Icon name="CalendarCheck" size={10} /> Déjà dans l&apos;agenda
          </a>
        ) : checking ? (
          <span className="flex items-center gap-1 rounded-md border border-white/10 px-2.5 py-1 text-[10px] font-semibold text-[var(--color-muted)]">
            <Icon name="Loader" size={10} className="animate-spin" /> Vérification…
          </span>
        ) : (
          <button
            type="button"
            onClick={create}
            disabled={state.status === "sending"}
            className="flex items-center gap-1 rounded-md bg-[#0a1410] px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-[#0a1410] disabled:opacity-50"
          >
            {state.status === "sending" ? (
              <>
                <Icon name="Loader" size={10} className="animate-spin" /> Ajout…
              </>
            ) : (
              <>
                <Icon name="Calendar" size={10} /> Ajouter à l&apos;agenda
              </>
            )}
          </button>
        )}
      </div>
      {state.status === "error" && (
        <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800">
          {state.error}
        </div>
      )}
    </div>
  );
}
